import React, { useState, useEffect } from 'react';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { listBuildings, createBuilding, updateBuilding, deleteBuilding } from '../services/buildings';
import { useT } from '../i18n';
import './BuildingsPage.css';

export const BuildingsPage = () => {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '', electricity_kwh: '', building_type: '', address: '', city: '', country: '', construction_year: '', floors: '', area_sqm: '', energy_sources: '', water_supply: '', heating_cooling: ''
  });
  const [form, setForm] = useState({
    name: '', electricity_kwh: '', building_type: '', address: '', city: '', country: '', construction_year: '', floors: '', area_sqm: '', energy_sources: '', water_supply: '', heating_cooling: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t('bldp.nameRequired');
    if (!form.building_type) errors.building_type = t('bldp.buildingTypeRequired');
    if (!form.address.trim()) errors.address = t('bldp.addressRequired');
    if (!form.city.trim()) errors.city = t('bldp.cityRequired');
    if (!form.country.trim()) errors.country = t('bldp.countryRequired');
    if (!form.construction_year) errors.construction_year = t('bldp.yearRequired');
    if (!form.area_sqm) errors.area_sqm = t('bldp.areaRequired');
    if (!form.floors) errors.floors = t('bldp.floorsRequired');
    if (!form.energy_sources) errors.energy_sources = t('bldp.energyRequired');
    if (!form.water_supply) errors.water_supply = t('bldp.waterRequired');
    if (!form.heating_cooling) errors.heating_cooling = t('bldp.heatingRequired');
    return errors;
  };

  const load = async () => {
    try {
      setItems(await listBuildings());
    } catch (err) {
      console.error('Failed to load buildings:', err);
      setError(t('bldp.loadFailed'));
    }
  };
  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    setSaving(true);
    setFieldErrors({});
    try {
      await createBuilding(form);
      setForm({ name:'', electricity_kwh:'', building_type:'', address:'', city:'', country:'', construction_year:'', floors:'', area_sqm:'', energy_sources:'', water_supply:'', heating_cooling:'' });
      await load();
    } catch (err) {
      console.error('Failed to create building:', err);
      if (err.response?.status === 403) {
        setError(err.response?.data?.message || t('bldp.planLimit'));
      } else {
        setError(err.response?.data?.message || t('bldp.addFailed'));
      }
    } finally { setSaving(false); }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditForm({
      name: b.name || '',
      electricity_kwh: b.electricity_kwh ?? '',
      building_type: b.building_type || '',
      address: b.address || '',
      city: b.city || '',
      country: b.country || '',
      construction_year: b.construction_year ?? '',
      floors: b.floors ?? '',
      area_sqm: b.area_sqm ?? '',
      energy_sources: b.energy_sources || '',
      water_supply: b.water_supply || '',
      heating_cooling: b.heating_cooling || ''
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({ name:'', electricity_kwh:'', building_type:'', address:'', city:'', country:'', construction_year:'', floors:'', area_sqm:'', energy_sources:'', water_supply:'', heating_cooling:'' }); };
  const saveEdit = async (id) => {
    setError('');
    try {
      await updateBuilding(id, editForm);
      await load();
      cancelEdit();
    } catch (err) {
      console.error('Failed to update building:', err);
      setError(err.response?.data?.message || t('bldp.updateFailed'));
    }
  };

  const remove = async (id) => {
    setError('');
    try {
      await deleteBuilding(id);
      await load();
    } catch (err) {
      console.error('Failed to delete building:', err);
      setError(err.response?.data?.message || t('bldp.deleteFailed'));
    }
  };

  return (
    <Shell sidebar={<Sidebar />}>
      <Navbar pageTitle={t('bldp.pageTitle')} />
      <div className="buildings-page">
        {/* Header */}
        <div className="buildings-header">
          <h2>{t('bldp.registrationTitle')}</h2>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="buildings-error-alert">
            <span>{error}</span>
            <button onClick={() => setError('')} className="buildings-error-close">×</button>
          </div>
        )}

        {/* Main Grid */}
        <div className="buildings-grid">
          {/* Left Card - Form */}
          <div className="buildings-card">
            <h3>{t('bldp.addBuilding')}</h3>

            <form onSubmit={add}>
              {/* Row 1: Name & Building Type */}
              <div className="buildings-form-row">
                <div className="buildings-form-field">
                  <label>{t('bldp.name')} <span className="required">*</span></label>
                  <input name="name" value={form.name} onChange={handleChange} className={fieldErrors.name ? 'input-error' : ''} />
                  {fieldErrors.name && <div className="field-error-message">{fieldErrors.name}</div>}
                </div>
                <div className="buildings-form-field">
                  <label>{t('bldp.building')} <span className="required">*</span></label>
                  <select name="building_type" value={form.building_type} onChange={handleChange} className={fieldErrors.building_type ? 'input-error' : ''}>
                    <option value="">{t('bldp.selectType')}</option>
                    <option value="Objekt tregtar">{t('bldp.typeCommercial')}</option>
                    <option value="Objekt industrial">{t('bldp.typeIndustrial')}</option>
                    <option value="Objekt banimi">{t('bldp.typeResidential')}</option>
                    <option value="Zyrë">{t('bldp.typeOffice')}</option>
                  </select>
                  {fieldErrors.building_type && <div className="field-error-message">{fieldErrors.building_type}</div>}
                </div>
              </div>

              {/* Row 2: Address & City */}
              <div className="buildings-form-row">
                <div className="buildings-form-field">
                  <label>{t('bldp.address')} <span className="required">*</span></label>
                  <input name="address" value={form.address} onChange={handleChange} className={fieldErrors.address ? 'input-error' : ''} />
                  {fieldErrors.address && <div className="field-error-message">{fieldErrors.address}</div>}
                </div>
                <div className="buildings-form-field">
                  <label>{t('bldp.city')} <span className="required">*</span></label>
                  <input name="city" value={form.city} onChange={handleChange} className={fieldErrors.city ? 'input-error' : ''} />
                  {fieldErrors.city && <div className="field-error-message">{fieldErrors.city}</div>}
                </div>
              </div>

              {/* Row 3: Country & Construction Year */}
              <div className="buildings-form-row">
                <div className="buildings-form-field">
                  <label>{t('bldp.country')} <span className="required">*</span></label>
                  <input name="country" value={form.country} onChange={handleChange} className={fieldErrors.country ? 'input-error' : ''} />
                  {fieldErrors.country && <div className="field-error-message">{fieldErrors.country}</div>}
                </div>
                <div className="buildings-form-field">
                  <label>{t('bldp.constructionYear')} <span className="required">*</span></label>
                  <input name="construction_year" type="number" value={form.construction_year} onChange={handleChange} className={fieldErrors.construction_year ? 'input-error' : ''} />
                  {fieldErrors.construction_year && <div className="field-error-message">{fieldErrors.construction_year}</div>}
                </div>
              </div>

              {/* Row 4: Area & Floors */}
              <div className="buildings-form-row">
                <div className="buildings-form-field">
                  <label>{t('bldp.areaSqm')} <span className="required">*</span></label>
                  <div className="buildings-input-with-unit">
                    <input name="area_sqm" type="number" value={form.area_sqm} onChange={handleChange} className={fieldErrors.area_sqm ? 'input-error' : ''} />
                    <span className="buildings-unit-badge">m²</span>
                  </div>
                  {fieldErrors.area_sqm && <div className="field-error-message">{fieldErrors.area_sqm}</div>}
                </div>
                <div className="buildings-form-field">
                  <label>{t('bldp.floors')} <span className="required">*</span></label>
                  <input name="floors" type="number" value={form.floors} onChange={handleChange} className={fieldErrors.floors ? 'input-error' : ''} />
                  {fieldErrors.floors && <div className="field-error-message">{fieldErrors.floors}</div>}
                </div>
              </div>

              {/* Row 5: Energy Sources & Water Supply */}
              <div className="buildings-form-row">
                <div className="buildings-form-field">
                  <label>{t('bldp.energySources')} <span className="required">*</span></label>
                  <select name="energy_sources" value={form.energy_sources} onChange={handleChange} className={fieldErrors.energy_sources ? 'input-error' : ''}>
                    <option value="">{t('bldp.selectSource')}</option>
                    <option value="Rrjet elektrik">{t('bldp.energyGrid')}</option>
                    <option value="Gaz natyror">{t('bldp.energyNaturalGas')}</option>
                    <option value="Naftë">{t('bldp.energyOil')}</option>
                    <option value="Energji diellore">{t('bldp.energySolar')}</option>
                  </select>
                  {fieldErrors.energy_sources && <div className="field-error-message">{fieldErrors.energy_sources}</div>}
                </div>
                <div className="buildings-form-field">
                  <label>{t('bldp.waterSupplyType')} <span className="required">*</span></label>
                  <select name="water_supply" value={form.water_supply} onChange={handleChange} className={fieldErrors.water_supply ? 'input-error' : ''}>
                    <option value="">{t('bldp.selectType')}</option>
                    <option value="Rrjet publik">{t('bldp.waterPublic')}</option>
                    <option value="Pus">{t('bldp.waterWell')}</option>
                    <option value="Sistem i pavarur">{t('bldp.waterIndependent')}</option>
                  </select>
                  {fieldErrors.water_supply && <div className="field-error-message">{fieldErrors.water_supply}</div>}
                </div>
              </div>

              {/* Row 6: Heating/Cooling */}
              <div className="buildings-form-row single">
                <div className="buildings-form-field">
                  <label>{t('bldp.heatingCoolingType')} <span className="required">*</span></label>
                  <select name="heating_cooling" value={form.heating_cooling} onChange={handleChange} className={fieldErrors.heating_cooling ? 'input-error' : ''}>
                    <option value="">{t('bldp.selectType')}</option>
                    <option value="Qendror">{t('bldp.heatingCentral')}</option>
                    <option value="Individual">{t('bldp.heatingIndividual')}</option>
                    <option value="Nuk ka">{t('bldp.heatingNone')}</option>
                  </select>
                  {fieldErrors.heating_cooling && <div className="field-error-message">{fieldErrors.heating_cooling}</div>}
                </div>
              </div>

              {/* Submit Button */}
              <div className="buildings-submit-wrapper">
                <button type="submit" disabled={saving} className="buildings-submit-btn">
                  <span>+</span>
                  {saving ? t('bldp.saving') : t('bldp.addBuildingSubmit')}
                </button>
              </div>
            </form>
          </div>

          {/* Right Card - Buildings List */}
          <div className="buildings-card">
            <h3>{t('bldp.yourBuildings')}</h3>

            {/* Table Header */}
            <div className="buildings-list-header">
              <span>{t('bldp.name')}</span>
              <span>{t('bldp.edit')}</span>
              <span>{t('bldp.delete')}</span>
            </div>

            {/* Buildings List */}
            <div className="buildings-list">
              {items.map(b => (
                <div key={b.id} className="buildings-list-item">
                  <div className="buildings-list-item-info">
                    <div className="buildings-list-item-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="buildings-list-item-text">
                      <div className="buildings-list-item-name">{b.name || '(unnamed)'}</div>
                      <div className="buildings-list-item-location">{b.city || 'city'}, {b.country || 'country'}</div>
                    </div>
                  </div>
                  <button onClick={() => startEdit(b)} className="buildings-edit-btn">Edit</button>
                  <button onClick={() => remove(b.id)} className="buildings-delete-btn">Delete</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {editingId && (
          <div className="buildings-modal-overlay">
            <div className="buildings-modal">
              <h3>Edit Building</h3>
              
              <form onSubmit={(e)=>{e.preventDefault();saveEdit(editingId);}}>
                <div className="buildings-modal-form">
                  <input 
                    placeholder="Name"
                    value={editForm.name} 
                    onChange={(e)=>setEditForm({...editForm,name:e.target.value})}
                  />
                  <input 
                    placeholder="Building Type"
                    value={editForm.building_type} 
                    onChange={(e)=>setEditForm({...editForm,building_type:e.target.value})}
                  />
                  <input 
                    placeholder="Address"
                    value={editForm.address} 
                    onChange={(e)=>setEditForm({...editForm,address:e.target.value})}
                  />
                  <input 
                    placeholder="City"
                    value={editForm.city} 
                    onChange={(e)=>setEditForm({...editForm,city:e.target.value})}
                  />
                  <input 
                    placeholder="Country"
                    value={editForm.country} 
                    onChange={(e)=>setEditForm({...editForm,country:e.target.value})}
                  />
                </div>

                <div className="buildings-modal-actions">
                  <button type="submit" className="buildings-modal-save">Save</button>
                  <button type="button" onClick={cancelEdit} className="buildings-modal-cancel">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
};
